<template>
    <div>
        <refill-fintech-recommendation
            v-if="recommendation"
            :recommendation="recommendation"
            @dismiss="dismissRecommendation"
        />

        <div v-else>
        <b-alert variant="danger" show v-if="error">
            {{ error }}
        </b-alert>

        <b-alert variant="danger" show v-if="acceptJsError">
            {{ acceptJsError }}
        </b-alert>

        <div class="d-flex justify-content-between align-items-center mt-2">
            <div>
                {{ $t("First Name") }}
            </div>
            <div>
                <b-btn
                    v-if="onboardingUser.authorizeProfile"
                    @click="cancelCreateCard"
                    variant="link"
                >
                    {{ $t("Cancel") }}
                </b-btn>
            </div>
        </div>
        <b-form-input
            v-model="firstName"
            autocomplete="given-name"
            :placeholder="$t('First Name')"
        />

        <div class="mt-2">
            {{ $t("Last Name") }}
        </div>
        <b-form-input
            v-model="lastName"
            autocomplete="family-name"
            :placeholder="$t('Last Name')"
        />

        <div class="mt-2">
            {{ $t("Country") }}
        </div>
        <b-select
            v-model="countryId"
            :options="countriesMapped"
            autocomplete="off"
            @change="onCountryChange"
        >
        </b-select>

        <div class="mt-2">
            {{ $t("Street Address") }}
        </div>
        <b-form-input
            v-model="address"
            autocomplete="street-address"
            :placeholder="$t('Street Address')"
        />

        <b-row class="mt-2">
            <b-col cols="6">
                <div>
                    {{ $t("City") }}
                </div>
                <b-form-input
                    v-model="city"
                    autocomplete="address-level2"
                    :placeholder="$t('City')"
                />
            </b-col>
            <b-col cols="6">
                <div>
                    {{ stateLabel }}
                </div>
                <b-form-select
                    v-if="hasStates"
                    v-model="billingState"
                    :options="selectedCountry.states"
                    autocomplete="address-level1"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled>
                            {{ stateLabel }}
                        </b-form-select-option>
                    </template>
                </b-form-select>
                <b-form-input
                    v-else
                    v-model="billingState"
                    autocomplete="address-level1"
                    :placeholder="stateLabel"
                />
            </b-col>
        </b-row>

        <div v-if="!skipPostcode">
            <div class="mt-2">
                {{ zipOrPostalCode }}
            </div>
            <b-form-input
                type="text"
                v-model="postalCode"
                autocomplete="postal-code"
                :placeholder="zipOrPostalCode"
            />
        </div>

        <div class="mt-2">
            {{ $t("Email") }}
        </div>
        <b-form-input
            v-model="email"
            type="email"
            autocomplete="email"
            :placeholder="$t('Email')"
        />

        <div class="mt-2">
            {{ $t("Phone") }}
        </div>
        <b-form-input
            v-model="phone"
            type="tel"
            autocomplete="tel"
            :placeholder="$t('Phone')"
        />

        <transition name="fade">
            <div v-if="countdown > 0" class="mt-3">
                <div
                    class="d-flex justify-content-between align-items-center mb-1"
                >
                    <div class="small">
                        {{ $t("Please wait") }}
                        {{ countdown }}
                        {{ $t("seconds before retrying...") }}
                    </div>
                </div>

                <b-progress
                    :value="progressValue"
                    :max="throttleTotalSeconds || 1"
                    animated
                    height="0.75rem"
                />
                <div class="mt-1 small text-opaque">
                    {{
                        $t(
                            "This brief pause helps prevent accidental double-charges."
                        )
                    }}
                </div>
            </div>
        </transition>

        <b-btn
            v-if="scriptLoaded"
            variant="primary"
            size="lg"
            block
            pill
            class="mt-3"
            :disabled="!formReady || saving || countdown > 0"
            @click="clickHiddenAcceptButton"
        >
            {{ $t("Pay") }} ${{ price.amount }}
            <div class="small text-opaque">
                {{ price.localPrice }}
            </div>
        </b-btn>

        <div v-if="!scriptLoaded" class="text-center mt-3">
            <b-spinner small />
            {{ $t("Loading payment form...") }}
        </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

import RefillFintechRecommendation from "@/components/wallet/refill/RefillFintechRecommendation";

export default {
    components: {
        RefillFintechRecommendation,
    },
    data() {
        return {
            firstName: null,
            lastName: null,
            address: null,
            city: null,
            billingState: null,
            postalCode: null,
            countryId: null,
            email: null,
            phone: null,
            countdown: null,
            countdownInterval: null,
            throttleTotalSeconds: 0,
            unsubscribeToMutations: null,
        };
    },
    created() {
        if (this.onboardingUser.countryId) {
            this.countryId = this.onboardingUser.countryId;
        }

        this.unsubscribeToMutations = this.$store.subscribe((mutation) => {
            if (
                mutation.type === "authorizeHosted/setTokenData" &&
                mutation.payload
            ) {
                this.$store.dispatch("authorizeHosted/hostedTransaction", {
                    ...mutation.payload,
                    priceId: this.price.id,
                    countryId: this.countryId,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    address: this.address,
                    city: this.city,
                    state: this.billingState,
                    postalCode: this.postalCode,
                    email: this.email,
                    phone: this.phone,
                });
            }

            if (
                mutation.type === "authorizeHosted/setThrottle" &&
                mutation.payload
            ) {
                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                }

                const payloadSeconds = Number(mutation.payload) || 0;
                this.countdown = payloadSeconds;
                this.throttleTotalSeconds = payloadSeconds;

                if (this.countdown > 0) {
                    this.countdownInterval = setInterval(() => {
                        if (this.countdown > 0) {
                            this.countdown = this.countdown - 1;
                        } else {
                            clearInterval(this.countdownInterval);
                            this.countdownInterval = null;
                        }
                    }, 1000);
                }
            }
        });
    },
    beforeDestroy() {
        if (this.unsubscribeToMutations) {
            this.unsubscribeToMutations();
        }
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    },
    methods: {
        clickHiddenAcceptButton() {
            const hiddenBtn = document.querySelector("button.AcceptUI");
            if (hiddenBtn) {
                hiddenBtn.click();
            }
        },
        onCountryChange() {
            this.billingState = null;
        },
        cancelCreateCard() {
            this.$store.commit("wallet/setCreateCard", false);
        },
        dismissRecommendation() {
            this.$store.commit("authorizeHosted/setRecommendation", null);
        },
    },
    computed: {
        selectedCountry() {
            return this.countries.find(
                (country) => country.id === this.countryId
            );
        },
        skipPostcode() {
            return this.selectedCountry?.doesntUsePostcode;
        },
        zipOrPostalCode() {
            return this.selectedCountry?.postalLabel || "Postal Code";
        },
        stateLabel() {
            return this.selectedCountry?.stateLabel || "State / Region";
        },
        hasStates() {
            return this.selectedCountry?.states?.length > 0;
        },
        countriesMapped() {
            return this.countries.map((country) => ({
                value: country.id,
                text: country.name,
            }));
        },
        formReady() {
            return this.countryId && (this.postalCode || this.skipPostcode);
        },
        progressValue() {
            const totalSeconds = this.throttleTotalSeconds || 0;
            const remainingSeconds = Math.max(this.countdown || 0, 0);
            return totalSeconds > 0 ? totalSeconds - remainingSeconds : 0;
        },
        ...mapState("options", ["countries"]),
        ...mapState("wallet", ["price"]),
        ...mapState("authorizeHosted", [
            "scriptLoaded",
            "saving",
            "error",
            "acceptJsError",
            "recommendation",
        ]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
.text-opaque {
    opacity: 0.7;
}
</style>
